<template lang="pug">
  div(class="dynamic-textarea-wrapper")
    textarea(
      :value="value"
      @input="$emit('update:value', $event.target.value)"
      ref="textarea"
      class="dynamic-textarea"
      :class="textareaClasses"
      :style="textareaStyles"
      :placeholder="placeholder"
      :disabled="disableTextarea"
      @focus="onFocus"
      @blur="$emit('blur', value)"
      @keydown="onKeyDown"
    )
    v-btn(
      small
      icon
      class="dynamic-textarea__icon"
      @click.stop="handleIconClick"
      v-if="!xsDevice"
    )
      e-svg-icon(
        size="sm"
        :name="value && !canEdit ? 'text-symbol-primary' : 'text-symbol-gray'"
      )
</template>

<script>
import responsive from '~/mixins/pages/responsive'
import ESvgIcon from '~/components/elements/icons/e-svg-icon'

export default {
  name: 'TemplatesDynamicTextarea',
  components: { ESvgIcon },
  mixins: [responsive],
  props: {
    field: {
      type: Object,
      required: true
    },
    value: {
      type: String,
      default: null
    },
    canEdit: {
      type: Boolean,
      default: false
    },
    fontSize: {
      type: [String, Number],
      default: null
    },
    isResizing: {
      type: Boolean,
      default: false
    },
    isDisabled: {
      type: Boolean,
      default: false
    },
    hasError: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: null
    }
  },
  computed: {
    disableTextarea() {
      return (!this.field.documentFieldValue && this.field.documentFieldPreValue) || this.isDisabled
    },
    textareaStyles() {
      return {
        fontSize: this.xsDevice ? '7px' : this.fontSize + 'px'
      }
    },
    textareaClasses() {
      return {
        filled: this.value && !this.canEdit,
        'error-state': this.hasError
      }
    }
  },
  mounted() {
    this.setTextarea()
  },
  methods: {
    onFocus() {
      this.$store.commit('templatefields/SET_ACTIVE_FIELD_ID', this.field.id)
      this.$store.commit('templatefields/SET_TOOLBARS_MAP_FIELD_ID', {
        id: this.field.id,
        value: true
      })
    },
    onKeyDown(e) {
      if (e.ctrlKey && e.key === 'c') {
        this.$emit('copy')
      }
      if (e.ctrlKey && e.key === 'x') {
        this.$emit('cut')
      }
      if (e.key === 'Delete') {
        this.$emit('delete')
      }
    },
    setTextarea() {
      if (this.canEdit) {
        this.$emit('update:value', this.field.hint)
      } else if (this.field.documentFieldPreValue) {
        this.$emit('update:value', this.field.documentFieldPreValue)
      } else {
        this.$emit('update:value', this.field.documentFieldValue)
      }
    },
    handleIconClick() {
      this.$refs.textarea.focus()
    }
  }
}
</script>

<style scoped lang="scss">
.dynamic-textarea-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
}
.dynamic-textarea {
  width: 100%;
  height: 100%;
  font-weight: 300;

  border-radius: 8px;
  border: 1px solid $primary;
  background: rgba(255, 255, 255, 0.7);
  padding: 6px 24px 6px 8px;
  resize: none;
  outline: none;

  @media (max-width: map-get($breakpoints, 'sm')) {
    padding: 0;
    line-height: 1.3;
    text-align: center;
  }

  &::-webkit-scrollbar {
    @media (max-width: map-get($breakpoints, 'sm')) {
      width: 0;
      background: transparent;
    }
  }

  &.filled {
    background:
      linear-gradient(0deg, rgba(143, 37, 125, 0.12) 0%, rgba(143, 37, 125, 0.12)) 100%,
      rgba(255, 255, 255, 0.4);
    color: $primary;
  }

  &.error-state {
    border: 1px solid $danger;
    background: rgba(255, 23, 68, 0.12);
  }

  &::placeholder {
    color: $text-dark;
  }

  &__icon {
    width: 16px;
    height: 16px;

    position: absolute;
    top: 50%;
    right: 8px;
    transform: translateY(-50%);
    z-index: map-get($zIndexes, 'base');

    &::before {
      opacity: 0 !important;
    }
  }
}
</style>
